import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings, whatsappLink } from "@/lib/settings";
import { Turnstile } from "@/components/Turnstile";

const zones = ["Scandicci Centro", "Casellina", "Vingone", "Le Bagnese", "Firenze Isolotto", "Firenze Centro", "Firenze San Frediano", "Colline / Chianti", "Altro"];
const types = ["Appartamento", "Villa", "Attico", "Terratetto", "Villetta a schiera", "Commerciale", "Terreno"];
const states = ["Nuovo / Ristrutturato", "Buono stato", "Da ristrutturare"];

const field = "w-full px-4 py-3 rounded-xl border border-[#DCE6EF] bg-white text-[#1B2A41] focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40 focus:border-[#2472C8] transition";

function Consent({ checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-2 text-xs text-[#566476] leading-relaxed cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-[#2472C8]" data-testid={testid} />
      Ho letto l'informativa privacy e acconsento al trattamento dei dati per essere ricontattato (GDPR).
    </label>
  );
}

function Honeypot({ value, onChange }) {
  return (
    <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
      value={value} onChange={onChange}
      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
  );
}

export function ValutaForm({ compact }) {
  const s = useSettings();
  const [f, setF] = useState({ hp: "", type: types[0], zone: zones[0], surface: "", state: states[1], name: "", phone: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [tsToken, setTsToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Accetta l'informativa privacy per continuare.");
    setLoading(true);
    try {
      await api.post("/leads", {
        kind: "valutazione", name: f.name, email: f.email, phone: f.phone, message: f.message, hp: f.hp, turnstile_token: tsToken,
        data: { tipologia: f.type, zona: f.zone, superficie_mq: f.surface, stato: f.state },
      });
      toast.success("Richiesta inviata! Ti ricontatteremo entro 24h con una valutazione.");
      setF({ hp: "", type: types[0], zone: zones[0], surface: "", state: states[1], name: "", phone: "", email: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="valuta-form">
      <Honeypot value={f.hp} onChange={set("hp")} />
      <Turnstile onToken={setTsToken} />
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={f.type} onChange={set("type")} className={field} data-testid="valuta-type">{types.map((t) => <option key={t}>{t}</option>)}</select>
        <select value={f.zone} onChange={set("zone")} className={field} data-testid="valuta-zone">{zones.map((t) => <option key={t}>{t}</option>)}</select>
        <input value={f.surface} onChange={set("surface")} placeholder="Superficie stimata (m²)" className={field} data-testid="valuta-surface" />
        <select value={f.state} onChange={set("state")} className={field} data-testid="valuta-state">{states.map((t) => <option key={t}>{t}</option>)}</select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={f.name} onChange={set("name")} placeholder="Nome e Cognome *" className={field} data-testid="valuta-name" />
        <input required value={f.phone} onChange={set("phone")} placeholder="Telefono *" className={field} data-testid="valuta-phone" />
      </div>
      <input required type="email" value={f.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="valuta-email" />
      {!compact && <textarea value={f.message} onChange={set("message")} placeholder="Note aggiuntive (indirizzo, disponibilità, ecc.)" rows={3} className={field} data-testid="valuta-message" />}
      <Consent checked={consent} onChange={setConsent} testid="valuta-consent" />
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={loading} className="flex-1 bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="valuta-submit-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Richiedi valutazione gratuita
        </button>
        <a href={whatsappLink(s.whatsapp_number, "Salve, vorrei una valutazione del mio immobile.")} target="_blank" rel="noopener noreferrer" className="sm:w-auto text-center border border-[#25D366] text-[#128C43] font-semibold py-3.5 px-5 rounded-full hover:bg-[#25D366]/10 transition-colors" data-testid="valuta-whatsapp">WhatsApp</a>
      </div>
    </form>
  );
}

export function CondoForm({ compact }) {
  const s = useSettings();
  const [f, setF] = useState({ hp: "", address: "", units: "", features: "", name: "", phone: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [tsToken, setTsToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Accetta l'informativa privacy per continuare.");
    setLoading(true);
    try {
      await api.post("/leads", {
        kind: "condominio", name: f.name, email: f.email, phone: f.phone, message: f.message, hp: f.hp, turnstile_token: tsToken,
        data: { indirizzo: f.address, unita: f.units, caratteristiche: f.features },
      });
      toast.success("Richiesta inviata! Ti invieremo un preventivo trasparente su misura.");
      setF({ hp: "", address: "", units: "", features: "", name: "", phone: "", email: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="condo-form">
      <Honeypot value={f.hp} onChange={set("hp")} />
      <Turnstile onToken={setTsToken} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={f.address} onChange={set("address")} placeholder="Indirizzo condominio *" className={field} data-testid="condo-address" />
        <input value={f.units} onChange={set("units")} placeholder="Numero unità immobiliari" className={field} data-testid="condo-units" />
      </div>
      {!compact && <input value={f.features} onChange={set("features")} placeholder="Ascensore, giardino, riscaldamento centralizzato…" className={field} data-testid="condo-features" />}
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={f.name} onChange={set("name")} placeholder="Nome referente *" className={field} data-testid="condo-name" />
        <input required value={f.phone} onChange={set("phone")} placeholder="Telefono *" className={field} data-testid="condo-phone" />
      </div>
      <input required type="email" value={f.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="condo-email" />
      {!compact && <textarea value={f.message} onChange={set("message")} placeholder="Descrivi le esigenze del condominio" rows={3} className={field} data-testid="condo-message" />}
      <Consent checked={consent} onChange={setConsent} testid="condo-consent" />
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={loading} className="flex-1 bg-[#1B2A41] hover:bg-[#24344F] text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="condo-submit-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Richiedi preventivo gratuito
        </button>
        <a href={whatsappLink(s.whatsapp_number, "Salve, vorrei un preventivo per l'amministrazione del nostro condominio.")} target="_blank" rel="noopener noreferrer" className="sm:w-auto text-center border border-[#25D366] text-[#128C43] font-semibold py-3.5 px-5 rounded-full hover:bg-[#25D366]/10 transition-colors" data-testid="condo-whatsapp">WhatsApp</a>
      </div>
    </form>
  );
}

export function ContactForm({ subject = "Richiesta informazioni", whatsappMsg }) {
  const s = useSettings();
  const [f, setF] = useState({ hp: "", name: "", phone: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [tsToken, setTsToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Accetta l'informativa privacy per continuare.");
    setLoading(true);
    try {
      await api.post("/leads", {
        kind: "contatto", name: f.name, email: f.email, phone: f.phone, message: f.message, hp: f.hp, turnstile_token: tsToken,
        data: { oggetto: subject },
      });
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setF({ hp: "", name: "", phone: "", email: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="servizio-contact-form">
      <Honeypot value={f.hp} onChange={set("hp")} />
      <Turnstile onToken={setTsToken} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={f.name} onChange={set("name")} placeholder="Nome e Cognome *" className={field} data-testid="servizio-name" />
        <input required value={f.phone} onChange={set("phone")} placeholder="Telefono *" className={field} data-testid="servizio-phone" />
      </div>
      <input required type="email" value={f.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="servizio-email" />
      <textarea required value={f.message} onChange={set("message")} placeholder="Descrivi la tua richiesta (immobile, pratica, esigenza…)" rows={4} className={field} data-testid="servizio-message" />
      <Consent checked={consent} onChange={setConsent} testid="servizio-consent" />
      <div className="flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={loading} className="flex-1 bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="servizio-submit-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Invia richiesta
        </button>
        <a href={whatsappLink(s.whatsapp_number, whatsappMsg || "Salve, vorrei informazioni sui vostri servizi.")} target="_blank" rel="noopener noreferrer" className="sm:w-auto text-center border border-[#25D366] text-[#128C43] font-semibold py-3.5 px-5 rounded-full hover:bg-[#25D366]/10 transition-colors" data-testid="servizio-whatsapp">WhatsApp</a>
      </div>
    </form>
  );
}
